import { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { UserPlus, Trash2, Shield, AlertCircle, CheckCircle2, Users2, Upload } from "lucide-react";
import BulkAddStaff from "@/components/admin/BulkAddStaff";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const USER_TYPE_LABELS = {
  teacher: "Teacher",
  principal: "Principal",
  assistant_principal: "Assistant Principal",
  ea_staff: "EA Staff",
  coach: "Coach",
  driveline: "Driveline",
};

export default function StaffManager() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [userType, setUserType] = useState("teacher");
  const [gradeLevel, setGradeLevel] = useState("");
  const [apCohort, setApCohort] = useState("");
  const [message, setMessage] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingGrade, setEditingGrade] = useState("");

  const { data: staff = [], isLoading } = useQuery({
    queryKey: ["staff"],
    queryFn: async () => {
      const res = await axios.get(`${BACKEND_URL}/api/staff`, { withCredentials: true });
      return res.data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.post(`${BACKEND_URL}/api/staff`, payload, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setMessage({ type: "success", text: `Added ${data.name || data.email}. They can now sign in with Google.` });
      setEmail("");
      setName("");
      setApCohort("");
      queryClient.invalidateQueries(["staff"]);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: "error", text: typeof detail === "string" ? detail : "Failed to add staff." });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (userId) => {
      await axios.delete(`${BACKEND_URL}/api/staff/${userId}`, { withCredentials: true });
    },
    onSuccess: () => {
      setMessage({ type: "success", text: "Staff removed." });
      queryClient.invalidateQueries(["staff"]);
      setTimeout(() => setMessage(null), 4000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: "error", text: typeof detail === "string" ? detail : "Failed to remove staff." });
      setTimeout(() => setMessage(null), 4000);
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em.endsWith("@iltexas.org")) {
      setMessage({ type: "error", text: "Staff email must end with @iltexas.org" });
      return;
    }
    addMutation.mutate({
      email: em,
      name: name.trim() || null,
      user_type: userType,
      role: "user",
      teacher_grade_level: gradeLevel.trim() || null,
      ap_grade_cohort: userType === "assistant_principal" ? (apCohort || null) : null,
    });
  };

  const updateGradeMutation = useMutation({
    mutationFn: async ({ userId, gradeLevel }) => {
      const res = await axios.patch(
        `${BACKEND_URL}/api/staff/${userId}`,
        { teacher_grade_level: gradeLevel || null },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries(["staff"]);
      setMessage({ type: "success", text: "Grade level updated." });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: "error", text: typeof detail === "string" ? detail : "Update failed." });
      setTimeout(() => setMessage(null), 4000);
    },
  });

  // Reuses the pattern from Users.jsx updateAPCohortMutation (lines 87-115).
  const updateAPCohortMutation = useMutation({
    mutationFn: async ({ userId, cohort }) => {
      const res = await axios.patch(
        `${BACKEND_URL}/api/staff/${encodeURIComponent(userId)}`,
        { ap_grade_cohort: cohort },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["staff"]);
      setMessage({ type: "success", text: "AP grade cohort updated successfully!" });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: "error", text: typeof detail === "string" ? detail : "Failed to update AP cohort." });
      setTimeout(() => setMessage(null), 4000);
    },
  });

  const handleRemove = (person) => {
    if (person.role === "admin") return;
    if (confirm(`Remove ${person.name || person.email}? They will no longer be able to sign in.`)) {
      removeMutation.mutate(person.user_id);
    }
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users2 className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Staff Manager</h1>
              <p className="text-slate-600 mt-1">
                Add and remove staff who can sign in with Google.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowBulk(true)}
            className="text-slate-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Add
          </Button>
        </div>

        {message && (
          <Alert
            className={
              message.type === "success"
                ? "border-green-200 bg-green-50"
                : "border-red-200 bg-red-50"
            }
          >
            {message.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription
              className={message.type === "success" ? "text-green-900" : "text-red-800"}
            >
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add Staff
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@iltexas.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="name">Name (optional)</Label>
                <Input
                  id="name"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="type">Role</Label>
                <Select value={userType} onValueChange={setUserType}>
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(USER_TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="grade">Grade Level</Label>
                <Input
                  id="grade"
                  placeholder="e.g. 6 or K"
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                />
              </div>
              {userType === "assistant_principal" && (
                <div className="md:col-span-2">
                  <Label htmlFor="ap-cohort">AP Grade Cohort</Label>
                  <Select value={apCohort} onValueChange={setApCohort}>
                    <SelectTrigger id="ap-cohort" data-testid="ap-cohort-select">
                      <SelectValue placeholder="Select cohort (4-5 or 6-8)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4-5">Grades 4-5</SelectItem>
                      <SelectItem value="6-8">Grades 6-8</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="md:col-span-5">
                <Button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {addMutation.isPending ? "Adding..." : "Add Staff Member"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Current Staff</CardTitle>
              <Badge variant="secondary">{staff.length} total</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : staff.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No staff yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Grade Level</TableHead>
                    <TableHead>Grade Cohort</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.map((p) => (
                    <TableRow key={p.user_id}>
                      <TableCell className="font-medium">
                        {p.name || <span className="text-slate-400 italic">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{p.email}</TableCell>
                      <TableCell>
                        {p.role === "admin" ? (
                          <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                            <Shield className="w-3 h-3 mr-1" /> Administrator
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            {USER_TYPE_LABELS[p.user_type] || p.user_type || "Staff"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === p.user_id ? (
                          <div className="flex gap-1 items-center">
                            <Input
                              value={editingGrade}
                              onChange={(e) => setEditingGrade(e.target.value)}
                              placeholder="e.g. 6"
                              className="h-8 w-20 text-sm"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                updateGradeMutation.mutate({
                                  userId: p.user_id,
                                  gradeLevel: editingGrade,
                                })
                              }
                              disabled={updateGradeMutation.isPending}
                              className="h-8 px-2 text-green-700 hover:bg-green-50"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingId(null)}
                              className="h-8 px-2 text-slate-500"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingId(p.user_id);
                              setEditingGrade(p.teacher_grade_level || "");
                            }}
                            className="text-sm text-slate-700 hover:text-blue-600 hover:underline"
                            title="Click to edit"
                          >
                            {p.teacher_grade_level || (
                              <span className="text-slate-400 italic">— set</span>
                            )}
                          </button>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.user_type === "assistant_principal" ? (
                          <Select
                            value={p.ap_grade_cohort || ""}
                            onValueChange={(value) =>
                              updateAPCohortMutation.mutate({ userId: p.user_id, cohort: value })
                            }
                            disabled={updateAPCohortMutation.isPending}
                          >
                            <SelectTrigger className="w-28">
                              <SelectValue placeholder="Set Cohort" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="4-5">Grades 4-5</SelectItem>
                              <SelectItem value="6-8">Grades 6-8</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {p.last_login_at
                          ? new Date(p.last_login_at).toLocaleString()
                          : "Never"}
                      </TableCell>
                      <TableCell className="text-right">
                        {p.role !== "admin" && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemove(p)}
                            disabled={removeMutation.isPending}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Remove staff"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {showBulk && (
        <BulkAddStaff
          onClose={() => setShowBulk(false)}
          onSuccess={() => {
            setShowBulk(false);
            queryClient.invalidateQueries(["staff"]);
          }}
        />
      )}
    </div>
  );
}
